import { useEffect, useState } from 'react';

import { ListItemText, Menu, MenuItem } from '@mui/material';

import FilterButton from './FilterButton';
import { useFilterAnchor } from './useFilterAnchor';
import { AlgorithmStatus, AlgorithmStatuses } from '../../schemas';

import { FILTER_DEBOUNCE_TIME_MS } from '@/app/constants';
import { Checkbox } from '@/shared/components/checkbox';

interface AlgorithmStatusFilterProps {
  filterId: string;
  buttonAriaLabel: string;
  searchParamAlgorithmStatuses: AlgorithmStatuses;
  handleNavigation: (selectedAlgoStatuses: AlgorithmStatuses) => void;
}

const algorithmStatus: Record<AlgorithmStatus, string> = {
  active: 'Applied',
  pending: 'Pending Application',
  pending_removal: 'Pending Removal',
};

const AlgorithmStatusFilter = ({
  filterId,
  buttonAriaLabel,
  searchParamAlgorithmStatuses,
  handleNavigation,
}: AlgorithmStatusFilterProps) => {
  const { anchorEl, handleOpen, handleClose, open, popoverId } =
    useFilterAnchor(filterId);

  const [selectedAlgoStatuses, setSelectedAlgoStatuses] =
    useState<AlgorithmStatuses>(searchParamAlgorithmStatuses);
  const [isChangedInternally, setIsChangedInternally] = useState(false);

  const handleAlgoStatusChange = (status: AlgorithmStatus) => {
    setIsChangedInternally(true);
    setSelectedAlgoStatuses((prev) => {
      const prevAlgoStatuses = prev ?? [];

      if (prevAlgoStatuses.includes(status)) {
        const filteredStatuses = prevAlgoStatuses.filter((v) => v !== status);
        return filteredStatuses.length > 0 ? filteredStatuses : undefined;
      } else {
        return [...prevAlgoStatuses, status];
      }
    });
  };

  const handleSelectAll = () => {
    setIsChangedInternally(true);

    const allStatuses = Object.keys(algorithmStatus) as AlgorithmStatus[];
    const isAllSelected = allStatuses.every((id) =>
      selectedAlgoStatuses?.includes(id)
    );

    if (isAllSelected) {
      setSelectedAlgoStatuses(undefined);
    } else {
      setSelectedAlgoStatuses(allStatuses);
    }
  };

  const isAllSelected =
    (selectedAlgoStatuses?.length ?? 0) > 0 &&
    Object.keys(algorithmStatus).every((key) =>
      selectedAlgoStatuses?.includes(key as AlgorithmStatus)
    );

  useEffect(() => {
    setSelectedAlgoStatuses(searchParamAlgorithmStatuses);
    setIsChangedInternally(false);
  }, [searchParamAlgorithmStatuses]);

  useEffect(() => {
    if (!isChangedInternally) {
      return;
    }

    const timer = setTimeout(() => {
      setIsChangedInternally(false);
      handleNavigation(selectedAlgoStatuses);
    }, FILTER_DEBOUNCE_TIME_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [
    handleNavigation,
    isChangedInternally,
    searchParamAlgorithmStatuses,
    selectedAlgoStatuses,
  ]);

  return (
    <>
      <FilterButton
        isActive={!!selectedAlgoStatuses?.length}
        onClick={handleOpen}
        aria-label={buttonAriaLabel}
      />
      <Menu
        id={popoverId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem
          key='select-all'
          onClick={handleSelectAll}
          disableGutters
          sx={{
            py: 1.25,
            pr: 1.75,
            pl: 1,
          }}
        >
          <Checkbox
            id='select-all-statuses'
            checked={isAllSelected}
            indeterminate={
              !isAllSelected && (selectedAlgoStatuses?.length ?? 0) > 0
            }
          />
          <ListItemText
            primary='(Select All)'
            slotProps={{
              primary: { variant: 'body3', color: 'textTertiary' },
            }}
          />
        </MenuItem>

        {Object.entries(algorithmStatus).map(([statusKey, statusTitle]) => (
          <MenuItem
            key={statusKey}
            onClick={() => {
              handleAlgoStatusChange(statusKey as AlgorithmStatus);
            }}
            disableGutters
            sx={{
              py: 1.25,
              pr: 1.75,
              pl: 1,
            }}
          >
            <Checkbox
              id={statusKey}
              checked={
                selectedAlgoStatuses?.includes(statusKey as AlgorithmStatus) ??
                false
              }
            />
            <ListItemText
              primary={statusTitle}
              slotProps={{ primary: { variant: 'body2' } }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AlgorithmStatusFilter;
